import {
  Badge,
  Box,
  Button,
  Flex,
  Kbd,
  Popover,
  Separator,
  Spinner,
  Text,
  ThickCheckIcon,
  Tooltip,
} from '@radix-ui/themes';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useDebounce from '~/hooks/use-debounce';
import useOS from '~/hooks/use-os';
import { OnoBadge, OnoButton, OnoIcon } from '../Ono/elements';
import {
  BADGE_ICON_SIZE_SETTINGS,
  CommandInput,
  CommandItem,
  CommandList,
  StyledCategoryBox,
  StyledChevronDownIcon,
  StyledCommand,
  StyledPlaceholder,
  StyledTextMultiple,
  StyledTextSingle,
  StyledTooltipBox,
} from './elements';
import type {
  Category,
  ComboboxComponentProps,
  ComboboxContextValue,
  ComboboxLoadingComponentProps,
  ComboItems,
  Item,
  RenderCommandItemsProps,
  RenderLeftoverItemsTooltipContentProps,
  RenderSelectedItemProps,
  RenderTooltipProps,
} from './types';
import { ComboboxContext, useComboboxContext } from './use-combobox-context';

const OTHER_CATEGORY_LABEL = 'Other';

function isCategoryList(items: ComboItems): items is Category[] {
  const first = items[0];
  return typeof first === 'object' && first?.type === 'category';
}

function isItemList(items: ComboItems): items is Item[] {
  const first = items[0];
  return typeof first === 'object' && first?.type === 'item';
}

function sortSelectedItemsOnTop(items: Item[] | Category[], selected: Item[]): Item[] | Category[] {
  const selectedValues = new Set(selected.map(s => s.value));
  const selectedLabels = new Set(selected.map(s => s.label));

  function isSelected(item: Item) {
    return selectedValues.has(item.value) || selectedLabels.has(item.label);
  }

  if (isCategoryList(items)) {
    const selectedItems: Category[] = [];
    const unselectedItems: Category[] = [];
    for (const category of items) {
      const selectedCategoryItems: Item[] = [];
      const unselectedCategoryItems: Item[] = [];
      for (const item of category.items) {
        if (isSelected(item)) selectedCategoryItems.push(item);
        else unselectedCategoryItems.push(item);
      }
      if (selectedCategoryItems.length > 0) {
        const selectedCategory: Category = {
          ...category,
          items: selectedCategoryItems,
        };
        selectedItems.push(selectedCategory);
      }
      if (unselectedCategoryItems.length > 0) {
        const unselectedCategory: Category = {
          ...category,
          items: unselectedCategoryItems,
        };
        unselectedItems.push(unselectedCategory);
      }
    }
    return [...selectedItems, ...unselectedItems];
  }

  const selectedItems: Item[] = [];
  const unselectedItems: Item[] = [];
  for (const item of items) {
    if (isSelected(item)) selectedItems.push(item);
    else unselectedItems.push(item);
  }
  return [...selectedItems, ...unselectedItems];
}

function RenderCommandItems({ items: propItems }: RenderCommandItemsProps) {
  const {
    items: contextItems,
    itemHeight,
    textSize,
    size,
    selected,
    onSelect,
    renderValueAndLabel,
  } = useComboboxContext();

  const paddingX = size === '1' ? '2' : '3';
  const renderItems = propItems || contextItems;

  return renderItems.map(item => {
    if (item.type !== 'item') {
      console.warn(`Expected item type but got ${item.type}. Skipping this item.`);
      return null;
    }

    const selectedItem = selected.find(s => s.value === item.value);
    const isIndeterminate = !!selectedItem?.indeterminate;

    return (
      <CommandItem
        key={item.value}
        onSelect={() => onSelect(item)}
        data-ono-selected={!!selectedItem}
      >
        <Flex
          width='100%'
          minHeight={`${itemHeight}px`}
          px={paddingX}
          align='center'
          justify='between'
          gap='2'
        >
          {renderValueAndLabel && item.value !== item.label ? (
            <Flex
              direction='row'
              gap='2'
            >
              <Text size={size}>{item.value}</Text>
              <Text
                size={size}
                color='gray'
              >
                {item.label}
              </Text>
            </Flex>
          ) : (
            <Text size={textSize}>{item.label}</Text>
          )}

          <div>
            {!isIndeterminate && !!selectedItem && <ThickCheckIcon />}
            {isIndeterminate && '-'}
          </div>
        </Flex>
      </CommandItem>
    );
  });
}

function RenderCommandGroup() {
  const { items: categories, textSize } = useComboboxContext();

  return categories.map(category => {
    if (category.type !== 'category') {
      console.warn(`Expected category type but got ${category.type}. Skipping this item.`);
      return null;
    }

    return (
      <>
        <StyledCategoryBox data-ono-category>
          <Text
            size={textSize}
            weight='bold'
            color='gray'
          >
            {category.label}
          </Text>
        </StyledCategoryBox>
        <RenderCommandItems items={category.items} />
      </>
    );
  });
}

function LoadingCommandItems() {
  return (
    <Flex
      align='end'
      justify='center'
    >
      <Spinner size='1'>Loading</Spinner>
    </Flex>
  );
}

function CommandComponent() {
  const {
    items,
    textSize,
    itemHeight,
    showSearch,
    size,
    resetSelected,
    loading,
    startPaging,
    setSearch,
    showClear,
    selected,
    effectiveCloseOnSelect,
  } = useComboboxContext();
  const debouncedShowClear = useDebounce(selected.length > 0, 200, !effectiveCloseOnSelect);
  const os = useOS();

  const isCategory = items[0] && items[0].type === 'category';
  const ItemRenderer = isCategory ? RenderCommandGroup : RenderCommandItems;
  const metaKeyLabel = os.isMac ? 'cmd' : 'ctrl';

  const { ref, inView } = useInView();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const commandRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (inView && startPaging) {
      startPaging();
    }
  }, [inView, startPaging]);

  useEffect(() => {
    // DropdownMenu.Content restores focus to its trigger after closing.
    // We need to wait until after that happens before focusing the input.
    const id = setTimeout(() => {
      if (showSearch) {
        inputRef.current?.focus();
      } else {
        commandRef.current?.focus();
      }
    }, 100);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.key === 'c') {
        resetSelected();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (showSearch && document.activeElement !== inputRef.current) {
          inputRef.current?.focus();
        } else if (!showSearch && document.activeElement !== commandRef.current) {
          commandRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [resetSelected, showSearch]);

  return (
    <StyledCommand
      shouldFilter={false}
      ref={commandRef}
      tabIndex={showSearch ? undefined : -1}
    >
      <Flex
        gap='2'
        direction='column'
      >
        {showSearch && (
          <CommandInput
            ref={inputRef}
            placeholder='Search…'
            onValueChange={setSearch}
          />
        )}
        <CommandList>
          {!loading && items.length === 0 && (
            <Box height={`${itemHeight}px`}>
              <Flex
                align='center'
                justify='center'
                height='100%'
              >
                <Text size={textSize}>No items found.</Text>
              </Flex>
            </Box>
          )}
          <ItemRenderer />
          <Box ref={ref} />
          {loading && <LoadingCommandItems />}
        </CommandList>
        {debouncedShowClear && showClear && (
          <>
            <Separator
              size='4'
              my='2'
            />
            <Box
              mt='2'
              width='100%'
              asChild
            >
              <Button
                size={size}
                variant='ghost'
                onMouseDown={resetSelected}
                color='gray'
              >
                <Text
                  weight='bold'
                  color='gray'
                >
                  Clear <Kbd>{metaKeyLabel} + c</Kbd>
                </Text>
              </Button>
            </Box>
          </>
        )}
      </Flex>
    </StyledCommand>
  );
}

function PopoverComponent() {
  return (
    <Box
      p='2'
      asChild
    >
      <Popover.Content onOpenAutoFocus={e => e.preventDefault()}>
        <CommandComponent />
      </Popover.Content>
    </Box>
  );
}

function formatItem(selectedItem: string | number | Item, optionItems?: Item[] | Category[]): Item | undefined {
  function formatStringOrNumber(selectedItem: string | number): Item {
    if (!optionItems) return { value: selectedItem, label: selectedItem.toString(), type: 'item' };
    const flattenedOptionItems = optionItems.flatMap(item => (item.type === 'category' ? item.items : item));
    const foundItem = flattenedOptionItems.find(i => i.value === selectedItem || i.label === selectedItem);
    return foundItem || { value: selectedItem, label: selectedItem.toString(), type: 'item' };
  }

  if (!selectedItem) return undefined;
  if (typeof selectedItem === 'string' || typeof selectedItem === 'number') return formatStringOrNumber(selectedItem);
  if (selectedItem.value && selectedItem.label) return { ...selectedItem, type: 'item' };
  return undefined;
}

function normalizeItemsAndCategories(
  items: ComboItems | Item | Category | string | number | undefined,
): Item[] | Category[] {
  if (!items || (Array.isArray(items) && items.length === 0)) return [];
  if (typeof items === 'string' || typeof items === 'number') {
    const returnItem = formatItem(items);
    return returnItem ? [returnItem] : [];
  }
  if (typeof items === 'object' && !Array.isArray(items)) return [items] as Item[] | Category[];
  if (isCategoryList(items) || isItemList(items)) return items;

  const returnItems: Item[] = [];
  items.forEach(item => {
    const formattedItem = formatItem(item);
    if (formattedItem) returnItems.push(formattedItem);
  });
  return returnItems;
}

function reconcile(options: Item[] | Category[], selected: Item[]) {
  const flatOptions: Item[] = isCategoryList(options) ? options.flatMap(c => c.items) : options;

  const optionsByValue = new Map(flatOptions.map(o => [o.value, o]));

  const resolvedSelected = selected.map(s => optionsByValue.get(s.value) ?? s);

  const missing = resolvedSelected.filter(s => !optionsByValue.has(s.value));

  if (missing.length === 0) {
    return { options, selected: resolvedSelected };
  }

  if (!isCategoryList(options)) {
    return { options: [...options, ...missing], selected: resolvedSelected };
  }

  // If any of the selected items are missing, we need to add them to the "Other" category.
  const otherIndex = options.findIndex(c => c.label === OTHER_CATEGORY_LABEL);

  const resolvedOptions: Category[] =
    otherIndex === -1
      ? [...options, { type: 'category' as const, label: OTHER_CATEGORY_LABEL, items: missing }]
      : options.map((c, i) => (i === otherIndex ? { ...c, items: [...c.items, ...missing] } : c));

  return { options: resolvedOptions, selected: resolvedSelected };
}

function normalizeSelectedItems(
  selectedItems: string | number | Item | Item[] | string[] | number[] | undefined,
  optionItems?: Item[] | Category[],
): Item[] {
  if (!selectedItems || (Array.isArray(selectedItems) && selectedItems.length === 0)) return [];
  if (Array.isArray(selectedItems)) {
    const returnItems: Item[] = [];
    selectedItems.forEach(item => {
      const formattedItem = formatItem(item, optionItems);
      if (formattedItem) returnItems.push(formattedItem);
    });
    return returnItems;
  }
  const formattedItem = formatItem(selectedItems, optionItems);
  if (formattedItem) return [formattedItem];
  return [];
}

function searchFilter(item: Item, search: string): boolean {
  const lowerSearch = search.toLowerCase();
  return item.label.toLowerCase().includes(lowerSearch) || item.value?.toString().toLowerCase().includes(lowerSearch);
}

function filterItems(items: Item[] | Category[], search: string): Item[] | Category[] {
  if (!search) return items;

  if (isCategoryList(items)) {
    const filteredItems: Category[] = [];
    items.forEach(category => {
      const filteredCategoryItems = category.items.filter(i => searchFilter(i, search));
      if (filteredCategoryItems.length > 0) {
        filteredItems.push({
          ...category,
          items: filteredCategoryItems,
        });
      }
    });
    return filteredItems;
  }

  const filteredItems = items.filter(i => searchFilter(i, search));
  const exactMatchItem = filteredItems.find(i => i.value?.toString().toLowerCase() === search.toLowerCase());
  if (exactMatchItem) return [exactMatchItem, ...filteredItems.filter(i => i.value !== exactMatchItem.value)];
  return filteredItems;
}

function RenderTooltip({ content, showTooltip, children }: RenderTooltipProps) {
  if (!showTooltip) return children;
  return <Tooltip content={<StyledTooltipBox>{content}</StyledTooltipBox>}>{children}</Tooltip>;
}

function RenderSelectedItem({ item, asAny }: RenderSelectedItemProps) {
  const { onSelect, size, disabled } = useComboboxContext();

  const ref = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (ref.current) {
      const { offsetWidth, scrollWidth } = ref.current;
      setShowTooltip(scrollWidth > offsetWidth);
    }
  }, [item.label]);

  return (
    <RenderTooltip
      key={item.value}
      content={item.label}
      showTooltip={showTooltip}
    >
      <OnoBadge
        variant={item.indeterminate ? 'outline' : 'soft'}
        disabled={disabled}
      >
        <StyledTextMultiple
          ref={ref}
          weight='bold'
          truncate
        >
          {item.label}
        </StyledTextMultiple>
        {!asAny && (
          <OnoIcon
            {...BADGE_ICON_SIZE_SETTINGS[size]}
            inline
            onClick={() => onSelect(item)}
            disabled={disabled}
            hoveredColor='var(--neutral-7)'
          />
        )}
      </OnoBadge>
    </RenderTooltip>
  );
}

function RenderLeftoverItemsTooltipContent({ leftoverItems }: RenderLeftoverItemsTooltipContentProps) {
  return (
    <Flex direction='column'>
      {leftoverItems.map(item => (
        <Text
          key={item.value}
          size='1'
        >
          {item.label + (item.indeterminate ? ' (Indeterminate)' : '')}
        </Text>
      ))}
    </Flex>
  );
}

function RenderSingleSelectedItem() {
  const { selected, placeholder, items, renderLabelInElementFareLeft, renderLabelInElementFareRight } =
    useComboboxContext();
  const ref = useRef<HTMLDivElement | null>(null);

  const selectedItem = selected?.[0];
  const label = useMemo(() => {
    if (!selectedItem || !items) return;
    if (selectedItem.label) return selectedItem.label;

    if (isCategoryList(items)) {
      const flattenedItems = items.flatMap(c => c.items);
      const renderableItem = flattenedItems.find(i => i.value === selectedItem.value);
      return renderableItem?.label;
    }

    const renderableItem = items.find(i => i.value === selectedItem.value);
    return renderableItem?.label;
  }, [selectedItem, items]);
  if (!label && (!renderLabelInElementFareLeft || !renderLabelInElementFareRight))
    return <StyledPlaceholder weight='regular'>{placeholder}</StyledPlaceholder>;

  const showTooltip = !!ref.current && ref.current.offsetWidth < ref.current.scrollWidth;

  return (
    <RenderTooltip
      content={label}
      showTooltip={showTooltip}
    >
      <StyledTextSingle
        ref={ref}
        truncate
        weight='regular'
        highContrast={!renderLabelInElementFareRight}
      >
        {label}
      </StyledTextSingle>
    </RenderTooltip>
  );
}

function RenderMultiableSelectedItems() {
  const { selected, maxVisibleItems, placeholder, renderLabelInElementFareLeft, showAnyIfNoValueIsSelected } =
    useComboboxContext();

  if (!renderLabelInElementFareLeft && selected.length === 0)
    return <StyledPlaceholder weight='regular'>{placeholder}</StyledPlaceholder>;

  const itemsToRender = maxVisibleItems ? selected.slice(0, maxVisibleItems) : selected;
  const leftoverItems = maxVisibleItems && selected.length > maxVisibleItems ? selected.slice(maxVisibleItems) : [];

  if (itemsToRender.length === 0 && showAnyIfNoValueIsSelected) {
    const item: Item = { value: 'any', label: 'Any', type: 'item' };
    return (
      <RenderSelectedItem
        key={item.value}
        item={item}
        asAny
      />
    );
  }

  return (
    <Flex
      gap='1'
      overflowX='hidden'
    >
      {itemsToRender.map(item => (
        <RenderSelectedItem
          key={item.value}
          item={item}
        />
      ))}
      {leftoverItems.length > 0 && (
        <Tooltip content={<RenderLeftoverItemsTooltipContent leftoverItems={leftoverItems} />}>
          <Badge>
            <StyledTextMultiple
              weight='bold'
              truncate
            >
              +{leftoverItems.length}
            </StyledTextMultiple>
          </Badge>
        </Tooltip>
      )}
    </Flex>
  );
}

export function ComboboxLoadingComponent({
  size = '2',
  variant = 'outline',
  width = '100%',
}: ComboboxLoadingComponentProps) {
  return (
    <Box
      width={width}
      asChild
    >
      <OnoButton
        variant={variant}
        size={size}
        disabled={true}
        color='gray'
      >
        <Flex
          gap='2'
          justify='between'
          align='center'
          width='100%'
        >
          <StyledPlaceholder weight='regular'>Loading...</StyledPlaceholder>
          <StyledChevronDownIcon color='gray' />
        </Flex>
      </OnoButton>
    </Box>
  );
}

function handleOnSelect(item: Item, prevSelected: Item[], isMultiSelect: boolean): Item[] {
  const itemWasSelected = prevSelected.find(selected => selected.value === item.value);
  if (!isMultiSelect) return itemWasSelected ? [] : [item];
  const newItem = { ...item };
  if (newItem.indeterminate) newItem.indeterminate = false;
  return itemWasSelected ? prevSelected.filter(selected => selected.value !== item.value) : [...prevSelected, newItem];
}

const ComboboxComponent = forwardRef(function ComboboxComponent(
  {
    items,
    defaultSelected,
    multiple = false,
    disabled = false,
    showSearch = true,
    onSelectedChange,
    size = '2',
    variant = 'outline',
    width = '100%',
    maxVisibleItems,
    placeholder = 'Select...',
    closeOnSelect,
    loading,
    startPaging,
    error,
    renderValueAndLabel = false,
    allowDeselect = true,
    showClear = true,
    renderLabelInElementFareLeft = false,
    renderLabelInElementFareRight = false,
    label,
    defaultOpen = false,
    showSingleItemAsMultipleItem = false,
    showAnyIfNoValueIsSelected = false,
    onComboboxRemove,
    selectedItemsOnTop = false,
  }: ComboboxComponentProps,
  ref,
) {
  if ((renderLabelInElementFareLeft || renderLabelInElementFareRight) && !label) {
    console.warn(
      `${
        renderLabelInElementFareLeft ? 'renderLabelInElementFareLeft' : 'renderLabelInElementFareRight'
      } is true but no label is provided. The label will not be shown in the list.`,
    );
  }

  const normalizedOptionItems = useMemo(() => normalizeItemsAndCategories(items), [items]);
  const normalizedSelectedItems = useMemo(
    () => normalizeSelectedItems(defaultSelected, normalizedOptionItems),
    [defaultSelected, normalizedOptionItems],
  );

  const { options: standardizedOptionItems, selected: standardizedSelectedItems } = useMemo(
    () => reconcile(normalizedOptionItems, normalizedSelectedItems),
    [normalizedOptionItems, normalizedSelectedItems],
  );

  const [selectedItems, setSelectedItems] = useState(standardizedSelectedItems);
  const [sortedItems, setSortedItems] = useState(standardizedOptionItems);
  const [isOpen, setIsOpen] = useState(false);
  const blockCloseUntilRef = useRef(0);

  useEffect(() => {
    if (!selectedItemsOnTop) {
      return setSortedItems(standardizedOptionItems);
    }
    if (!isOpen) {
      const newSortedItems = sortSelectedItemsOnTop(standardizedOptionItems, standardizedSelectedItems);
      return setSortedItems(newSortedItems);
    }
    if (sortedItems.length !== standardizedOptionItems.length) {
      setSortedItems(standardizedOptionItems);
    }
  }, [standardizedOptionItems, standardizedSelectedItems, selectedItemsOnTop, isOpen, sortedItems.length]);

  useEffect(() => {
    if (defaultOpen) {
      // Defer opening until the DropdownMenu's click event has finished propagating.
      // Then block closes for 200ms to absorb the spurious onPointerDownOutside /
      // onFocusOutside events that Radix fires as it returns focus to the
      // DropdownMenu trigger (via rAF / setTimeout) right as our Popover opens.
      const id = setTimeout(() => {
        blockCloseUntilRef.current = Date.now() + 200;
        setIsOpen(true);
      }, 0);
      return () => clearTimeout(id);
    }
  }, []);
  const [search, setSearch] = useState('');
  const debouncedLoading = useDebounce(loading, 400, value => value === true);
  const effectiveCloseOnSelect = closeOnSelect ?? !multiple;

  function handleSetIsOpen(value: boolean) {
    if (value === false) {
      if (Date.now() < blockCloseUntilRef.current) return;
      setTimeout(() => setSearch(''), 100); // delay to allow closing animation
    }
    setIsOpen(value);
  }

  useEffect(() => {
    if (loading) return;

    if (selectedItems.length === 0) {
      const hasDefaults = Array.isArray(defaultSelected) ? defaultSelected.length > 0 : !!defaultSelected;
      if (hasDefaults) setSelectedItems(normalizeSelectedItems(defaultSelected, sortedItems));
      return;
    }

    // If defaultSelected resolved to a placeholder item before `items` had loaded,
    // patch in the real label once the matching option becomes available.
    const flatOptions = isCategoryList(sortedItems) ? sortedItems.flatMap(c => c.items) : sortedItems;
    const optionsByValue = new Map(flatOptions.map(o => [o.value, o]));

    setSelectedItems(prevSelected => {
      let changed = false;
      const updated = prevSelected.map(item => {
        const match = optionsByValue.get(item.value);
        if (match && match.label !== item.label) {
          changed = true;
          return { ...item, label: match.label };
        }
        return item;
      });
      return changed ? updated : prevSelected;
    });
  }, [loading, selectedItems, defaultSelected, sortedItems]);

  function resetSelected() {
    setSelectedItems([]);
    onSelectedChange(multiple ? [] : undefined);
  }

  function onSelect(item: Item) {
    if (disabled) return;

    setSelectedItems(prevSelected => {
      const isSameAsPreviousValue = prevSelected.some(selected => selected.value === item.value);
      if (!allowDeselect && !multiple && isSameAsPreviousValue) return prevSelected;
      const nextSelected = handleOnSelect(item, prevSelected, multiple);
      onSelectedChange(multiple ? nextSelected : nextSelected[0]);
      return nextSelected;
    });
    if (closeOnSelect ?? !multiple) handleSetIsOpen(false);
  }

  const renderableItems = useMemo(() => filterItems(sortedItems, search), [sortedItems, search]);

  const contextValue: ComboboxContextValue = {
    items: renderableItems,
    selected: selectedItems,
    onSelect,
    resetSelected,
    showSearch,
    size,
    textSize: size === '1' ? '1' : '2',
    itemHeight: size === '1' ? 24 : 32,
    maxVisibleItems,
    placeholder,
    startPaging,
    loading: debouncedLoading,
    search,
    setSearch,
    renderValueAndLabel,
    disabled,
    showClear,
    effectiveCloseOnSelect,
    renderLabelInElementFareLeft,
    renderLabelInElementFareRight,
    showAnyIfNoValueIsSelected,
  };

  const ItemRenderer =
    multiple || showSingleItemAsMultipleItem ? RenderMultiableSelectedItems : RenderSingleSelectedItem;

  useImperativeHandle(ref, () => ({
    clearSelected: resetSelected,
  }));

  return (
    <ComboboxContext.Provider value={contextValue}>
      <Popover.Root
        open={isOpen}
        onOpenChange={handleSetIsOpen}
      >
        <Popover.Trigger>
          <Box
            width={width}
            asChild
          >
            <OnoButton
              variant={variant}
              size={size}
              disabled={disabled}
              color='gray'
              error={error}
            >
              <Flex
                gap='2'
                justify='between'
                align='center'
                width='100%'
              >
                {renderLabelInElementFareLeft && label && (
                  <Text
                    weight='regular'
                    size={size}
                    highContrast
                  >
                    {label}
                  </Text>
                )}
                <ItemRenderer />
                {renderLabelInElementFareRight && label && (
                  <Text
                    weight='regular'
                    size={size}
                    ml='-4px' // Make gap look like a space
                    color='gray'
                  >
                    {label}
                  </Text>
                )}
                {onComboboxRemove ? (
                  <OnoIcon
                    name='x-badge-size-3'
                    width={16}
                    height={16}
                    inline
                    onClick={e => {
                      e.stopPropagation();
                      onComboboxRemove();
                    }}
                    hoveredColor='var(--neutral-5)'
                  />
                ) : (
                  <StyledChevronDownIcon color='gray' />
                )}
              </Flex>
            </OnoButton>
          </Box>
        </Popover.Trigger>
        <PopoverComponent />
      </Popover.Root>
    </ComboboxContext.Provider>
  );
});

export default ComboboxComponent;
